import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface GyroType {
  name: string;
  precision: string;
  application: string;
  color: string;
}

interface GyroApplication {
  name: string;
  icon: string;
  desc: string;
  color: string;
}

interface MEMSLayer {
  label: string;
  color: string;
  y: number;
  h: number;
}

interface GyroscopeTemplateProps {
  topic: string;
  subtitle: string;
  gyroTypes: GyroType[];
  applications: GyroApplication[];
  memsLayers: MEMSLayer[];
  stats: { value: string; label: string }[];
  bottomInsight: string;
}

const DEFAULT_PROPS: GyroscopeTemplateProps = {
  topic: '陀螺仪与角动量',
  subtitle: 'Gyroscope & Angular Momentum',
  gyroTypes: [
    {
      name: '机械陀螺',
      precision: '精度 ~1°/h',
      application: '早期航海、飞机姿态仪',
      color: '#94a3b8',
    },
    {
      name: '光纤陀螺',
      precision: '精度 0.001°/h',
      application: '高精度惯性导航系统',
      color: '#d4a574',
    },
    {
      name: '激光陀螺',
      precision: '精度 0.0001°/h',
      application: '战略导弹、航天器定姿',
      color: '#3b82f6',
    },
    {
      name: 'MEMS陀螺',
      precision: '精度 ~10°/h',
      application: '手机、无人机、游戏手柄',
      color: '#22c55e',
    },
  ],
  applications: [
    { name: '飞机姿态', icon: '✈️', desc: '三轴陀螺测量俯仰/横滚/偏航', color: '#3b82f6' },
    { name: '卫星定姿', icon: '🛰️', desc: '控制矩量轮维持姿态稳定', color: '#d4a574' },
    { name: '手机IMU', icon: '📱', desc: '6轴传感器实现屏幕旋转与导航', color: '#22c55e' },
    { name: '导弹制导', icon: '🎯', desc: '惯性导航不依赖外部信号', color: '#ef4444' },
  ],
  memsLayers: [
    { label: '盖板', color: '#64748b', y: 0, h: 28 },
    { label: '检测电极', color: '#3b82f6', y: 40, h: 28 },
    { label: '检测质量', color: '#d4a574', y: 80, h: 30 },
    { label: '驱动弹簧', color: '#22c55e', y: 122, h: 28 },
    { label: '基底', color: '#94a3b8', y: 162, h: 28 },
  ],
  stats: [
    { value: '0.001°/h', label: '光纤陀螺精度' },
    { value: '<1mm', label: 'MEMS陀螺尺寸' },
    { value: '3轴', label: '每部手机含陀螺' },
  ],
  bottomInsight: '角动量守恒是宇宙的基本法则——从旋转的银河到手中的陀螺',
};

export const GyroscopeTemplate: React.FC<Partial<GyroscopeTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const gyroDraw = interpolate(frame, [10, 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const rotorSpin = interpolate(frame, [0, 460], [0, 360 * 8], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const precessionAngle = interpolate(frame, [50, 460], [0, 360 * 2], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const typeStart = 55;
  const appStart = 100;
  const precessionStart = 160;
  const conserveStart = 190;
  const memsStart = 260;
  const nutationStart = 300;
  const statsStart = 360;
  const insightStart = 410;

  const insightOpacity = interpolate(frame, [insightStart, insightStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const skaterArmWidth = interpolate(frame, [conserveStart, conserveStart + 60], [60, 20], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const skaterSpinSpeed = interpolate(frame, [conserveStart, conserveStart + 60], [1, 4], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const skaterRotation = interpolate(
    frame,
    [conserveStart, conserveStart + 120],
    [0, 360 * skaterSpinSpeed],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const nutationWobble = Math.sin(
    interpolate(frame, [nutationStart, nutationStart + 120], [0, Math.PI * 12], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
  ) * 5;

  const outerGimbalAngle = interpolate(frame, [30, 460], [0, 15], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const middleGimbalAngle = interpolate(frame, [30, 460], [0, -10], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a0a14 0%, #141428 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Subtle particle field */}
      {Array.from({ length: 45 }).map((_, i) => {
        const x = (i * 191) % 1920;
        const y = (i * 277) % 1080;
        const size = 1.5 + (i % 3);
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#3b82f6',
              opacity: 0.06,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 48,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
        }}
      >
        <div
          style={{
            padding: '5px 16px',
            background: '#1e1b4b',
            borderRadius: 8,
            fontSize: 22,
            fontWeight: 700,
            color: '#3b82f6',
            border: '1px solid #3b82f630',
            whiteSpace: 'nowrap',
          }}
        >
          🌀 角动量物理
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, color: '#f1f5f9', whiteSpace: 'nowrap' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
      </div>

      {/* Key formula badge */}
      <div
        style={{
          position: 'absolute',
          top: 32,
          right: 48,
          textAlign: 'right',
          opacity: headerIn,
        }}
      >
        <div style={{ fontSize: 24, color: '#64748b' }}>核心公式</div>
        <div style={{ fontSize: 38, fontWeight: 900, color: '#3b82f6' }}>L = Iω</div>
      </div>

      {/* ===== LEFT PANEL: Gyroscope Types ===== */}
      <div style={{ position: 'absolute', top: 150, left: 48, width: 340 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          陀螺仪类型
        </div>
        {p.gyroTypes.map((gt, i) => {
          const s = spring({
            frame: frame - (typeStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f0f22',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 10,
                border: `1px solid ${gt.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: gt.color }}>{gt.name}</span>
                <span style={{ fontSize: 20, color: '#94a3b8', fontFamily: 'monospace' }}>{gt.precision}</span>
              </div>
              <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>
                {gt.application}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== CENTER: Gyroscope SVG — 3-axis gimbal with spinning rotor ===== */}
      <svg
        style={{ position: 'absolute', left: 560, top: 130 }}
        width="440"
        height="440"
        viewBox="0 0 440 440"
      >
        <defs>
          <linearGradient id="rotorGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="50%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
        </defs>

        {/* Outer gimbal ring */}
        <g opacity={gyroDraw} transform={`rotate(${outerGimbalAngle}, 220, 220)`}>
          <ellipse
            cx="220"
            cy="220"
            rx="190"
            ry="190"
            fill="none"
            stroke="#d4a574"
            strokeWidth="4"
            strokeDasharray="12 6"
          />
          <circle cx="220" cy="30" r="6" fill="#d4a574" />
          <circle cx="220" cy="410" r="6" fill="#d4a574" />
        </g>

        {/* Middle gimbal ring */}
        <g opacity={gyroDraw} transform={`rotate(${middleGimbalAngle}, 220, 220)`}>
          <ellipse
            cx="220"
            cy="220"
            rx="145"
            ry="145"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3.5"
            strokeDasharray="10 5"
            transform="rotate(90, 220, 220)"
          />
          <circle cx="75" cy="220" r="5" fill="#3b82f6" />
          <circle cx="365" cy="220" r="5" fill="#3b82f6" />
        </g>

        {/* Inner gimbal ring */}
        <g opacity={gyroDraw}>
          <ellipse
            cx="220"
            cy="220"
            rx="100"
            ry="100"
            fill="none"
            stroke="#22c55e"
            strokeWidth="3"
          />
        </g>

        {/* Spinning rotor disc */}
        <g transform={`rotate(${rotorSpin}, 220, 220)`} opacity={gyroDraw}>
          <ellipse
            cx="220"
            cy="220"
            rx="65"
            ry="65"
            fill="url(#rotorGrad)"
            fillOpacity="0.15"
            stroke="#94a3b8"
            strokeWidth="2.5"
          />
          {[0, 45, 90, 135].map((a) => (
            <line
              key={a}
              x1={220 + 20 * Math.cos((a * Math.PI) / 180)}
              y1={220 + 20 * Math.sin((a * Math.PI) / 180)}
              x2={220 + 62 * Math.cos((a * Math.PI) / 180)}
              y2={220 + 62 * Math.sin((a * Math.PI) / 180)}
              stroke="#94a3b8"
              strokeWidth="1.5"
              opacity="0.5"
            />
          ))}
          <circle cx="220" cy="220" r="8" fill="#94a3b8" />
        </g>

        {/* Angular momentum vector L arrow */}
        <g opacity={interpolate(frame, [50, 80], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
          <line x1="220" y1="220" x2="220" y2="60" stroke="#22c55e" strokeWidth="3" />
          <polygon points="220,45 212,65 228,65" fill="#22c55e" />
          <text x="236" y="60" fill="#22c55e" fontSize="24" fontWeight="700">L</text>
        </g>

        {/* Spin ω symbol */}
        <g opacity={interpolate(frame, [50, 80], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
          <text x="295" y="225" fill="#3b82f6" fontSize="24" fontWeight="700">ω</text>
          <path
            d="M290,210 A15,15 0 1,1 280,225"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="1.5"
            markerEnd="url(#spinArrow)"
          />
          <defs>
            <marker id="spinArrow" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
              <polygon points="0,0 6,3 0,6" fill="#3b82f6" />
            </marker>
          </defs>
        </g>
      </svg>

      {/* ===== RIGHT PANEL: Applications ===== */}
      <div style={{ position: 'absolute', top: 150, right: 48, width: 340 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          工程应用
        </div>
        {p.applications.map((app, i) => {
          const s = spring({
            frame: frame - (appStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0f0f22',
                borderRadius: 12,
                padding: '12px 14px',
                marginBottom: 10,
                border: `1px solid ${app.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 26, flexShrink: 0 }}>{app.icon}</span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: app.color }}>{app.name}</div>
                <div style={{ fontSize: 20, color: '#94a3b8', lineHeight: 1.4, marginTop: 2 }}>
                  {app.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Row: Precession, Conservation, Bicycle Wheel, Nutation ===== */}
      <div style={{
        position: 'absolute',
        top: 620,
        left: 48,
        right: 48,
        display: 'flex',
        gap: 16,
      }}>
        {/* 进动 Precession */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
            进动 Precession
          </div>
          <svg style={{ width: '100%' }} viewBox="0 0 380 200">
            <g transform="translate(10, 10)">
              <ellipse
                cx="180"
                cy="95"
                rx="110"
                ry="40"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
                strokeDasharray="6 4"
                opacity={interpolate(frame, [precessionStart, precessionStart + 40], [0, 0.7], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              />
              {(() => {
                const angle = (precessionAngle * Math.PI) / 180;
                const dotX = 180 + 110 * Math.cos(angle);
                const dotY = 95 + 40 * Math.sin(angle);
                return (
                  <circle
                    cx={dotX}
                    cy={dotY}
                    r="8"
                    fill="#ef4444"
                    opacity={interpolate(frame, [precessionStart, precessionStart + 30], [0, 1], {
                      extrapolateLeft: 'clamp',
                      extrapolateRight: 'clamp',
                    })}
                  />
                );
              })()}
              <g opacity={interpolate(frame, [precessionStart + 10, precessionStart + 40], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })}>
                <line x1="180" y1="140" x2="180" y2="170" stroke="#d4a574" strokeWidth="2.5" />
                <polygon points="180,176 174,164 186,164" fill="#d4a574" />
                <text x="196" y="172" fill="#d4a574" fontSize="20" fontWeight="600">τ 力矩</text>
              </g>
              <g opacity={interpolate(frame, [precessionStart + 20, precessionStart + 50], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })}>
                <text x="295" y="92" fill="#ef4444" fontSize="20" fontWeight="600">进动方向</text>
                <text x="180" y="26" fill="#64748b" fontSize="20" textAnchor="middle">
                  τ ⊥ L → 进动
                </text>
              </g>
            </g>
          </svg>
        </div>

        {/* 角动量守恒 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
            角动量守恒
          </div>
          <svg style={{ width: '100%' }} viewBox="0 0 380 200">
            <g transform="translate(0, 10)">
              <g transform={`rotate(${skaterRotation % 360}, 110, 100)`}>
                <line x1="110" y1="50" x2="110" y2="135" stroke="#94a3b8" strokeWidth="3.5" />
                <circle cx="110" cy="38" r="15" fill="none" stroke="#94a3b8" strokeWidth="3" />
                <line
                  x1={110 - skaterArmWidth}
                  y1="78"
                  x2={110 + skaterArmWidth}
                  y2="78"
                  stroke="#94a3b8"
                  strokeWidth="3"
                />
                <line x1="110" y1="135" x2="98" y2="168" stroke="#94a3b8" strokeWidth="3" />
                <line x1="110" y1="135" x2="122" y2="168" stroke="#94a3b8" strokeWidth="3" />
              </g>
              <text
                x="180"
                y="58"
                fill="#3b82f6"
                fontSize="20"
                fontWeight="600"
                opacity={interpolate(frame, [conserveStart, conserveStart + 30], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                收臂 → 转速加快
              </text>
              <text
                x="180"
                y="88"
                fill="#64748b"
                fontSize="20"
                opacity={interpolate(frame, [conserveStart + 10, conserveStart + 40], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                I↓ → ω↑（L守恒）
              </text>
              <text
                x="180"
                y="132"
                fill="#22c55e"
                fontSize="20"
                fontWeight="600"
                opacity={interpolate(frame, [conserveStart + 30, conserveStart + 60], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                L = Iω = const
              </text>
            </g>
          </svg>
        </div>

        {/* 自行车轮演示 */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
            自行车轮演示
          </div>
          <svg style={{ width: '100%' }} viewBox="0 0 380 200">
            <g transform="translate(0, 15)">
              <g opacity={interpolate(frame, [conserveStart + 40, conserveStart + 70], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              })}>
                <circle cx="100" cy="80" r="65" fill="none" stroke="#94a3b8" strokeWidth="3.5" />
                <circle cx="100" cy="80" r="8" fill="#94a3b8" />
                {[0, 60, 120, 180, 240, 300].map((a) => (
                  <line
                    key={a}
                    x1={100 + 8 * Math.cos((a * Math.PI) / 180)}
                    y1={80 + 8 * Math.sin((a * Math.PI) / 180)}
                    x2={100 + 63 * Math.cos((a * Math.PI) / 180)}
                    y2={80 + 63 * Math.sin((a * Math.PI) / 180)}
                    stroke="#94a3b8"
                    strokeWidth="1.2"
                    opacity="0.5"
                  />
                ))}
                <rect x="85" y="68" width="30" height="24" rx="5" fill="none" stroke="#d4a574" strokeWidth="2" />
              </g>
              <text
                x="173"
                y="59"
                fill="#d4a574"
                fontSize="20"
                fontWeight="600"
                opacity={interpolate(frame, [conserveStart + 50, conserveStart + 80], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                握住旋转的车轮轴
              </text>
              <text
                x="173"
                y="90"
                fill="#ef4444"
                fontSize="20"
                fontWeight="600"
                opacity={interpolate(frame, [conserveStart + 60, conserveStart + 90], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                倾斜轴 → 感受进动力
              </text>
              <text
                x="173"
                y="120"
                fill="#64748b"
                fontSize="20"
                opacity={interpolate(frame, [conserveStart + 70, conserveStart + 100], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                力矩方向 ⊥ 角动量方向
              </text>
            </g>
          </svg>
        </div>

        {/* 章动 Nutation */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
            章动 Nutation
          </div>
          <svg style={{ width: '100%' }} viewBox="0 0 380 200">
            <g transform="translate(30, 5)">
              <ellipse
                cx="160"
                cy="95"
                rx="105"
                ry="38"
                fill="none"
                stroke="#ef444460"
                strokeWidth="2"
                strokeDasharray="5 3"
                opacity={interpolate(frame, [nutationStart, nutationStart + 30], [0, 0.6], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              />
              {(() => {
                const nProg = interpolate(frame, [nutationStart, nutationStart + 120], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                });
                const points: string[] = [];
                const steps = Math.floor(nProg * 200);
                for (let s = 0; s <= steps; s++) {
                  const t = (s / 200) * Math.PI * 4;
                  const px = 160 + 105 * Math.cos(t) + 10 * Math.sin(t * 6);
                  const py = 95 + 38 * Math.sin(t) + 10 * Math.cos(t * 6);
                  points.push(`${px.toFixed(1)},${py.toFixed(1)}`);
                }
                if (points.length < 2) return null;
                return (
                  <polyline
                    points={points.join(' ')}
                    fill="none"
                    stroke="#a855f7"
                    strokeWidth="2.5"
                    opacity="0.8"
                  />
                );
              })()}
              {(() => {
                const nAngle = interpolate(frame, [nutationStart, nutationStart + 120], [0, Math.PI * 4], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                });
                const dx = 160 + 105 * Math.cos(nAngle) + nutationWobble;
                const dy = 95 + 38 * Math.sin(nAngle) + nutationWobble;
                return (
                  <circle
                    cx={dx}
                    cy={dy}
                    r="7"
                    fill="#a855f7"
                    opacity={interpolate(frame, [nutationStart, nutationStart + 20], [0, 1], {
                      extrapolateLeft: 'clamp',
                      extrapolateRight: 'clamp',
                    })}
                  />
                );
              })()}
              <text
                x="160"
                y="162"
                fill="#a855f7"
                fontSize="20"
                textAnchor="middle"
                opacity={interpolate(frame, [nutationStart + 20, nutationStart + 50], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
              >
                进动 + 微小振荡 = 章动
              </text>
            </g>
          </svg>
        </div>
      </div>

      {/* ===== MEMS Cross-Section ===== */}
      <div style={{ position: 'absolute', top: 150, left: 1020, width: 500 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          MEMS陀螺结构
        </div>
        <svg width="460" height="220" viewBox="0 0 460 220">
          {p.memsLayers.map((ml, i) => {
            const layerIn = spring({
              frame: frame - (memsStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <g key={i} opacity={layerIn}>
                <rect
                  x="10"
                  y={ml.y + 10}
                  width="260"
                  height={ml.h}
                  rx="3"
                  fill={`${ml.color}30`}
                  stroke={ml.color}
                  strokeWidth="1.5"
                />
                <text
                  x="290"
                  y={ml.y + 10 + ml.h / 2 + 5}
                  fill={ml.color}
                  fontSize="22"
                  fontWeight="600"
                >
                  {ml.label}
                </text>
              </g>
            );
          })}
          {/* Coriolis arrow */}
          <g opacity={interpolate(frame, [memsStart + 60, memsStart + 90], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}>
            <text x="388" y="100" fill="#ef4444" fontSize="16" fontWeight="700">科里奥利</text>
            <text x="388" y="120" fill="#ef4444" fontSize="16" fontWeight="700">力检测</text>
            <line x1="380" y1="116" x2="275" y2="116" stroke="#ef4444" strokeWidth="2" strokeDasharray="5 3" markerEnd="url(#coriolisArrow)" />
            <defs>
              <marker id="coriolisArrow" markerWidth="8" markerHeight="8" refX="8" refY="4" orient="auto">
                <polygon points="0,0 8,4 0,8" fill="#ef4444" />
              </marker>
            </defs>
          </g>
        </svg>
      </div>


      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 48,
          right: 48,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0f22',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e293b',
                opacity: stIn,
                transform: `scale(${0.85 + 0.15 * stIn})`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6 }}>
                <span style={{ fontSize: 30, fontWeight: 900, color: '#3b82f6' }}>{st.value}</span>
                <span style={{ fontSize: 24, color: '#64748b' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Insight (OPAQUE background) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 48,
          right: 48,
          background: '#0f0f22',
          borderRadius: 14,
          padding: '16px 22px',
          borderTop: '3px solid #3b82f6',
          opacity: insightOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🌀</span>
          <span style={{ fontSize: 20, color: '#f1f5f9', lineHeight: 1.6 }}>{p.bottomInsight}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 460], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #94a3b8, #3b82f6, #22c55e, #ef4444)',
        }}
      />
    </AbsoluteFill>
  );
};
